import { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import styled from 'styled-components';
import 'swiper/css';
import 'swiper/css/pagination';
import ArrowLeftDesktop from './assets/desktop/arrow-left.svg';
import ArrowRightDesktop from './assets/desktop/arrow-right.svg';
import ArrowLeftMobile from './assets/mobile/arrow-left.svg';
import ArrowRightMobile from './assets/mobile/arrow-right.svg';

import { ImageGalleryFragment } from '@/__generated__/graphql';
import { getLanguage } from '@/lib/config';
import { BorderRadius, breakpoints, colors, imageLoaderResponsive, mediaQueries } from '@/theme/lib';
import { useMobile } from '@lib/hooks/useMobile';
import Image from "next/legacy/image";
import { SwiperProps, SwiperSlideProps } from 'swiper/react';
import { SwiperModule } from 'swiper/types';

const StyledImageGallery = styled.div`
  height: 100%;
  display: flex;
  align-items: center;

  .swiper-wrapper {
    list-style: none;
  }
  .swiper {
    margin: 0 auto;
    width: 100vw;
    ${mediaQueries.md} {
      ${props => BorderRadius(props.theme.config.cards.generic.imageBorderRadius.desktop)}
      overflow: hidden;
    }
    ${mediaQueries.lg} {
      width: 80vw;
      aspect-ratio: 1528 / 800;
    }
  }
  .image-gallery__card {
    height: 100%;
    min-height: 480px;
    overflow: hidden;
    display: flex;
    img {
      object-fit: cover;
    }
  }
  .image-gallery__pagination {
    color: ${colors.white};
    position: absolute;
    top: 0;
    padding-top: ${(props) => props.theme.key === 'zsl' ? '0.625rem;' : '1.625rem'};
    left: ${(props) => props.theme.key === 'zsl' ? ' -0.625rem' : ' -1.625rem;'};
    text-align: right;
    z-index: 2;
    font-size: 0.75rem;
    ${mediaQueries.md} {
      padding-top: ${(props) => props.theme.key === 'zsl' ? '0.625rem;' : '2.625rem'};
    }
    ${mediaQueries.lg} {
      padding-top: 0.625rem;
      top: auto;
      bottom: 3.625rem;
      left: -4.75rem;
      font-size: 0.875rem;
    }
  }
  .card__image-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    &::after {
      position: absolute;
      content: '';
      width: 100%;
      height: 40%;
      bottom: 0;
      left: 0;
      background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 100%);
      opacity: 0.8;
    }
    ${mediaQueries.lg} {
      ${BorderRadius('8px')}
    }
  }
  .card__image {
    position: relative;
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .card__text {
    color: ${colors.white};
    z-index: 1;
    margin: auto 1.125rem 1.75rem;
    ${mediaQueries.md} {
      margin: auto 1.125rem 3.75rem;
    }
    ${mediaQueries.lg} {
      margin: auto 4.875rem 3.625rem;
      width: 37%;
    }
  }
  .card__title {
    margin-bottom: 0.5rem;
    ${mediaQueries.lg} {
      margin-bottom: 0.75rem;
    }
  }
  .card__caption {
    font-size: 0.75rem;
    line-height: 120%;
    ${mediaQueries.lg} {
      font-size: 0.875rem;
    }
  }
`;

const StyledArrowWrapper = styled.div`
  position: absolute;
  width: 100%;
  z-index: 3;
  top: 50%;
  transform: translateY(-50%);
  svg {
    circle {
      fill: ${(props) => props.theme.key !== 'zsl' ? colors.honeyBee : undefined};
    }
    path {
      fill: ${(props) => props.theme.key !== 'zsl' ? colors.zooFont : undefined};
    }
  }
`;

const StyledArrows = styled.div`
  padding: 0 1.125rem;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  button {
    background: none;
    border: none;
    height: auto;
    width: auto;
    cursor: pointer;
    margin: 0;
    padding: 0;
    svg {
        border-radius: 50%;
        ${(props) => props.theme.key !== 'zsl' &&
        `border: 1px solid ${props.theme.colors.imageGalleryPagination.paginationArrowCircleBorder};`
        }

        circle {
          fill: ${(props) => props.theme.colors.imageGalleryPagination.paginationArrowCircle};
        }
        path {
          fill: ${(props) => props.theme.colors.imageGalleryPagination.paginationArrowPath};
        }
        &:hover {
          circle {
            fill: ${(props) => props.theme.colors.imageGalleryPagination.paginationArrowCircleHover};
          }
        }
      }
  }
  button.swiper-button-disabled {
    opacity: 50%;
    cursor: default;
  }
  ${mediaQueries.lg} {
    padding: 0 4.75rem;
  }
`;

export default function ImageGallery(props: ImageGalleryFragment): ReactElement {
  const isMobile = useMobile();
  const language = getLanguage();

  // Dynamically load swiper.
  const [swiperModules, setSwiperModules] = useState<SwiperModule[]>([]);
  const [Swiper, setSwiper] = useState<FunctionComponent<SwiperProps> | null>(null);
  const [SwiperSlide, setSwiperSlide] = useState<FunctionComponent<SwiperSlideProps> | null>(null);

  useEffect(() => {
    const getSwiperModules = async () => {
      const modules = await import('swiper');
      const { Navigation, Pagination } = await import('swiper/modules');
      const swiperComponents = await import('swiper/react');

      setSwiperModules([Pagination, Navigation]);
      setSwiper(swiperComponents.Swiper);
      setSwiperSlide(swiperComponents.SwiperSlide);
    };

    getSwiperModules();
  }, []);
  // Dynamically load swiper - End.

  if (!props?.gallerySlides?.length) {
    return <></>;
  }

  if (swiperModules.length > 0 && Swiper && SwiperSlide) {
    return (
      <StyledImageGallery>
        <Swiper
          navigation={{ nextEl: `.image-gallery__next-${props.id}`, prevEl: `.image-gallery__prev-${props.id}` }}
          slidesPerView={1}
          modules={swiperModules}
          wrapperTag="ul"
          pagination={{ clickable: false, type: 'fraction', el: `.image-gallery__pagination-${props.id}` }}
        >
          <span className={`image-gallery__pagination image-gallery__pagination-${props.id}`} />
          <StyledArrowWrapper>
            <StyledArrows>
              <button className={`image-gallery__prev image-gallery__prev-${props.id}`}>
                {isMobile ? (
                  <ArrowLeftMobile alt="Previous slide" />
                ) : (
                  <ArrowLeftDesktop alt="Previous slide" />
                )}
              </button>
              <button className={`image-gallery__next image-gallery__next-${props.id}`}>
                {isMobile ? (
                  <ArrowRightMobile alt="Next slide" />
                ) : (
                  <ArrowRightDesktop alt="Next slide" />
                )}
              </button>
            </StyledArrows>
          </StyledArrowWrapper>
          {props.gallerySlides.map((card, index) => (
            <SwiperSlide key={index} tag="li">
              <div className="image-gallery__card">
                <div className="card__image-wrapper">
                  <Image
                    src={card.gallerySlide.url}
                    alt={card.gallerySlide.altText || ''}
                    layout="intrinsic"
                    objectFit="cover"
                    sizes={`(min-width: ${breakpoints.sm}px) 100vw,
                            (min-width: ${breakpoints.md}px) 100vw,
                            (min-width: ${breakpoints.lg}px) 1192px,
                            720px`}
                    width={isMobile ? 720 : 1528}
                    height={isMobile ? 960 : 800}
                    loader={imageLoaderResponsive(isMobile ? 720 : 1528, isMobile ? 960 : 800)}
                  />
                </div>
                <div className="card__text">
                  {card?.title && <h4 className="card__title" lang={language !== 'en' ? language : undefined}>{card.title}</h4>}
                  {card?.gallerySlideCaption && (
                    <div
                      className="card__caption"
                      dangerouslySetInnerHTML={{ __html: card.gallerySlideCaption }}
                      lang={language !== 'en' ? language : undefined}
                    />
                  )}
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </StyledImageGallery>
    );
  }
  else {
    return <>Loading...</>
  }
}
